import React from 'react';
import PropTypes from 'prop-types';
import IStores from '@meronex/icons/mdc/MdcWarehouse';
import IItem from '@meronex/icons/bi/BiSquareRounded';
import IEdit from '@meronex/icons/fi/FiEdit2';
import IArchive from '@meronex/icons/fi/FiArchive';
import IAudit from '@meronex/icons/fi/FiClipboard';
import ICsv from '@meronex/icons/fi/FiDownload';
import IQRCode from '@meronex/icons/fi/FiTag';
import IChart from '@meronex/icons/fi/FiBarChart2';
import IBox from '@meronex/icons/fi/FiPackage';
import IDollar from '@meronex/icons/fi/FiDollarSign';
import IActivity from '@meronex/icons/fi/FiActivity';
import IAlert from '@meronex/icons/fi/FiAlertTriangle';
import IFolder from '@meronex/icons/fi/FiFolder';
import IPlus from '@meronex/icons/fi/FiPlus';
import { App } from '@meronex/app';
import { toast } from '@meronex/components';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

import { Icons } from '../common/Icons';
import Block from '../common/Block';
import StatCard from '../common/StatCard';

import Stores from './Stores';
import View from '../views/components/View';
import StoresDialog from './components/stores/StoresDialog';
import EditStoreDialog from './components/stores/EditStoreDialog';
import AddStoreDialog from './components/stores/AddStoreDialog';
import StoreArchivesWarningsDialog from './components/stores/StoreArchivesWarningsDialog';
import StoreAnalyticsDialog from './components/storeAnalytics/StoreAnalyticsDialog';
import ArchivedStoresDialog from './components/stores/ArchivedStoresDialog';
import { storesMng } from './api/storesMng';
import { can, p } from '../common/roles';

function StoresView(props) {
  const { activeView } = props;
  const theme = useTheme();

  const permissions = {
    in: can(p.stores.itemIn),
    out: can(p.stores.itemOut),
    transfer: can(p.stores.transferItem),
    auditItem: can(p.stores.auditStore),
    restore: can(p.stores.itemRestore),
    delete: can(p.stores.itemDelete),
    modify: can(p.stores.modifyItemAmount),
    archive: can(p.stores.itemArchive),
    auditStore: can(p.stores.auditItem),
    CRUDItemTypes: can(p.stores.CRUDItemTypes),
    viewPrices: can(p.stores.viewStoresPrices),
  };
  // we fetch the store header items here
  const [openStoreDialog, setOpenStoreDialog] = React.useState(false);
  const [openEditStoreDialog, setOpenEditStoreDialog] = React.useState(false);
  const [openAddStoreDialog, setOpenAddStoreDialog] = React.useState(false);
  const [openWarningsDialog, setOpenWarningsDialog] = React.useState(false);
  const [archiveWarnings, setArchiveWarnings] = React.useState();
  const [archiving, setArchiving] = React.useState(false);
  const [stores, setStores] = React.useState(undefined);
  const [selectedStore, setSelectedStore] = React.useState();
  const [filterValue, setFilterValue] = React.useState('');
  const [stockFilter, setStockFilter] = React.useState(null);
  const [storesError, setStoresError] = React.useState(null);
  const [storesLoading, setStoresLoading] = React.useState(false);
  const [storeActions, setStoreActions] = React.useState(null);
  const [itemsLoaded, setItemsLoaded] = React.useState(false);
  const pendingActionRef = React.useRef(null);
  const storeActionsRef = React.useRef(null);
  const selectedStoreRef = React.useRef(null);
  const [openAnalyticsDialog, setOpenAnalyticsDialog] = React.useState(false);
  const [openArchivedStoresDialog, setOpenArchivedStoresDialog] = React.useState(false);
  const [openArchiveConfirm, setOpenArchiveConfirm] = React.useState(false);

  // Keep refs in sync for use in event handlers (avoid stale closures)
  React.useEffect(() => { storeActionsRef.current = storeActions; }, [storeActions]);
  React.useEffect(() => { selectedStoreRef.current = selectedStore; }, [selectedStore]);

  const updateUserSelectStore = async () => {
    // Handle empty stores array gracefully
    if (Array.isArray(stores) && stores.length === 0) {
      setSelectedStore(null);
      return;
    }

    const selectedStore = await storesMng.getUserSelectedStore(stores);
    setSelectedStore(selectedStore);
  };

  React.useEffect(() => {
    App.eventsManager.on('event-product-updated', StoresView.name, () => {
      init();
    });
    App.eventsManager.on('event-work-order-added', StoresView.name, () => {
      init();
    });
    App.eventsManager.on('event-work-order-reversed', StoresView.name, () => {
      init();
    });
    App.eventsManager.on('event-edit-store', StoresView.name, async (e) => {
      if (!e.storeId) return;
      let store = stores && stores.find((s) => s._id === e.storeId);
      if (!store) {
        const allStores = await storesMng.getStores();
        store = allStores && allStores.find((s) => s._id === e.storeId);
      }
      if (store) {
        setSelectedStore(store);
        setOpenEditStoreDialog(true);
      }
    });
    App.eventsManager.on('event-audit-store', StoresView.name, async (e) => {
      if (!e.storeId) return;
      let store = stores && stores.find((s) => s._id === e.storeId);
      if (!store) {
        const allStores = await storesMng.getStores();
        store = allStores && allStores.find((s) => s._id === e.storeId);
      }
      if (store) {
        // If this store is already selected and actions are ready, fire directly
        if (storeActionsRef.current && selectedStoreRef.current && selectedStoreRef.current._id === store._id) {
          storeActionsRef.current.auditStore();
        } else {
          setSelectedStore(store);
          pendingActionRef.current = 'auditStore';
        }
      }
    });
    App.eventsManager.on('event-store-action', StoresView.name, async (e) => {
      if (!e.storeId || !e.action) return;
      let store = stores && stores.find((s) => s._id === e.storeId);
      if (!store) {
        const allStores = await storesMng.getStores();
        store = allStores && allStores.find((s) => s._id === e.storeId);
      }
      if (store) {
        setSelectedStore(store);
        if (e.action === 'analytics') {
          setOpenAnalyticsDialog(true);
        } else if (e.action === 'exportCsv') {
          // If storeActions already available (store already selected), call directly.
          // Otherwise set pending — useEffect will fire when storeActions becomes available.
          if (storeActionsRef.current && selectedStoreRef.current && selectedStoreRef.current._id === store._id) {
            storeActionsRef.current.exportToCsv();
          } else {
            pendingActionRef.current = 'exportCsv';
          }
        }
      }
    });
    App.eventsManager.on('event-store-selected', StoresView.name, async (e) => {
      if (e.storeId) {
        let store = stores && stores.find((s) => s._id === e.storeId);
        if (!store) {
          const allStores = await storesMng.getStores();
          setStores(allStores);
          store = allStores && allStores.find((s) => s._id === e.storeId);
        }
        if (store) {
          setSelectedStore(store);
          setFilterValue(null);
        }
      } else {
        await updateUserSelectStore();
      }
    });
    return () => {
      App.eventsManager.removeListener('event-product-updated', StoresView.name);
      App.eventsManager.removeListener('event-work-order-added', StoresView.name);
      App.eventsManager.removeListener('event-work-order-reversed', StoresView.name);
      App.eventsManager.removeListener('event-edit-store', StoresView.name);
      App.eventsManager.removeListener('event-audit-store', StoresView.name);
      App.eventsManager.removeListener('event-store-action', StoresView.name);
      App.eventsManager.removeListener('event-store-selected', StoresView.name);
    };
  }, [stores]);

  // Execute pending action when storeActions becomes available
  React.useEffect(() => {
    if (storeActions && pendingActionRef.current) {
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      if (action === 'auditStore') {
        storeActions.auditStore();
      } else if (action === 'exportCsv') {
        storeActions.exportToCsv();
      }
    }
  }, [storeActions]);

  React.useEffect(() => {
    if (stores) {
      updateUserSelectStore();
    }
  }, [stores]);

  React.useEffect(() => {
    if (activeView !== 'stores') {
      setFilterValue(null);
    } else {
      fetchStores();
    }
  }, [activeView]);

  const init = async () => {
    if (!stores) {
      await fetchStores();
    }
  };

  const refresh = async () => {
    await fetchStores();
  };

  const fetchStores = async (cb) => {
    setStoresLoading(true);
    setStoresError(null);

    try {
      const [_stores, dashboard] = await Promise.all([
        storesMng.getStores(),
        storesMng.getDashboardData().catch(() => null),
      ]);

      // Ensure we always have an array
      const storesArray = Array.isArray(_stores) ? _stores : [];

      // Enrich stores with dashboard data (inventory value, 30-day tx count)
      if (dashboard) {
        for (const store of storesArray) {
          if (dashboard.inventoryValue && dashboard.inventoryValue[store._id] != null) {
            store.totalValue = dashboard.inventoryValue[store._id];
          }
          if (dashboard.storeVolume && dashboard.storeVolume[store._id] != null) {
            store.transactionCount = dashboard.storeVolume[store._id];
          }
          if (dashboard.stockAlerts && dashboard.stockAlerts[store._id]) {
            store.outOfStockCount = dashboard.stockAlerts[store._id].outOfStock || 0;
            store.lowStockCount = dashboard.stockAlerts[store._id].lowStock || 0;
          }
        }
      }

      setStores(storesArray);

      if (typeof cb === 'function') {
        cb(storesArray);
      }
    } catch (error) {
      console.error('fetchStores error:', error);
      setStoresError(error.message || 'Failed to load stores');
      // Set empty array on error to show empty state instead of loading forever
      setStores([]);
      if (typeof cb === 'function') {
        cb([]);
      }
    } finally {
      setStoresLoading(false);
    }
  };

  const onStoreSelect = async (store, cb) => {
    storesMng.setUserSelectedStore(store._id);
    setSelectedStore(store);
    setItemsLoaded(false);
    setFilterValue(null);
    setStockFilter(null);
    App.eventsManager.emit('event-reset-filter', StoresView.name);

    /*fetchStoreItems();*/
    if (cb) {
      cb();
    }
  };

  const archiveStore = () => {
    if (!selectedStore) return;
    if (stores.length <= 1) {
      toast.error('You need at least one active store.', { autoClose: 900 });
      return;
    }
    if (archiving) return;
    setOpenArchiveConfirm(true);
  };

  const confirmArchiveStore = async () => {
    setOpenArchiveConfirm(false);
    setArchiving(true);
    try {
      await storesMng.archiveStore(selectedStore._id);
      toast.success('Store archived successfully', { autoClose: 900 });
      App.eventsManager.emit('event-sites-loaded', StoresView.name);
      refreshAfterArchive();
    } catch (e) {
      console.error('Archive store error:', e);
      if (
        e && (e.error === 'archiveStore.destinationStore' ||
        e.error === 'archiveStore.productStoreItems')
      ) {
        setArchiveWarnings(e);
        setOpenWarningsDialog(true);
      } else if (e && e.reason) {
        toast.error(e.reason, { autoClose: 3000 });
      } else if (e && e.message) {
        toast.error(e.message, { autoClose: 3000 });
      } else {
        toast.error('Failed to archive store', { autoClose: 3000 });
      }
    } finally {
      setArchiving(false);
    }
  };

  const refreshAfterArchive = () => {
    if (Array.isArray(stores) && stores.length > 0) {
      fetchStores((updatedStores) => {
        if (updatedStores.length > 0) {
          onStoreSelect(updatedStores[0]);
        }
      });
    }
  };

  const isDark = theme.palette.type === 'dark';
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const PANEL_WIDTH = 280;

  const cardBorder = isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const mutedColor = isDark ? 'rgba(255,255,255,0.5)' : '#94a3b8';
  const accentColor = isDark ? 'rgba(99, 102, 241, 0.8)' : '#6366f1';
  const textColor = isDark ? 'rgba(255,255,255,0.8)' : '#475569';

  const sectionLabel = {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    color: accentColor,
    marginBottom: '12px',
  };

  const cardStyle = {
    borderRadius: '16px',
    border: `1px solid ${cardBorder}`,
    backgroundColor: cardBg,
    padding: '20px',
    marginBottom: '16px',
  };

  const panelItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '12px 14px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '14px',
    color: textColor,
    transition: 'background-color 0.15s ease',
  };

  const panelItemHoverBg = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc';

  const PanelItem = ({ icon, label, onClick, disabled, highlight }) => (
    <div
      onClick={disabled ? undefined : onClick}
      style={{
        ...panelItemStyle,
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'default' : 'pointer',
        color: highlight ? accentColor : textColor,
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.backgroundColor = panelItemHoverBg;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', color: highlight ? accentColor : mutedColor }}>
        {icon}
      </span>
      <span>{label}</span>
    </div>
  );

  const skFrom = isDark ? '#2a2a2e' : '#e2e8f0';
  const skTo = isDark ? '#3a3a40' : '#f1f5f9';

  const renderSidePanelSkeleton = () => {
    if (!isDesktop) return null;
    const Sk = ({ w, h, r = 8, style = {}, delay = 0 }) => (
      <div style={{
        width: w, height: h, borderRadius: r,
        backgroundColor: skFrom,
        animation: `skPulse 2s ease-in-out ${delay}s infinite`,
        ...style,
      }} />
    );
    return (
      <div style={{
        width: PANEL_WIDTH,
        minWidth: PANEL_WIDTH,
        padding: '16px 16px 16px 0',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Store info skeleton */}
        <div style={cardStyle}>
          <Sk w="70px" h="11px" r={3} delay={0} />
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            padding: '10px 14px', borderRadius: '10px',
            backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
            marginTop: '12px',
          }}>
            <Sk w="36px" h="36px" r={10} delay={0.05} />
            <div>
              <Sk w="80px" h="14px" r={4} delay={0.08} />
              <Sk w="55px" h="10px" r={3} delay={0.1} style={{ marginTop: 4 }} />
            </div>
          </div>
        </div>
        {/* Reports skeleton */}
        <div style={cardStyle}>
          <Sk w="55px" h="11px" r={3} delay={0.1} />
          {[0.15, 0.2, 0.25].map((d, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 14px' }}>
              <Sk w="18px" h="18px" r={4} delay={d} />
              <Sk w="100px" h="12px" r={4} delay={d + 0.03} />
            </div>
          ))}
        </div>
        {/* Store skeleton */}
        <div style={cardStyle}>
          <Sk w="45px" h="11px" r={3} delay={0.3} />
          {[0.35, 0.4].map((d, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 14px' }}>
              <Sk w="18px" h="18px" r={4} delay={d} />
              <Sk w="85px" h="12px" r={4} delay={d + 0.03} />
            </div>
          ))}
        </div>
        {/* Items skeleton */}
        <div style={cardStyle}>
          <Sk w="40px" h="11px" r={3} delay={0.45} />
          {[0.5, 0.55].map((d, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 14px' }}>
              <Sk w="18px" h="18px" r={4} delay={d} />
              <Sk w="90px" h="12px" r={4} delay={d + 0.03} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSidePanel = () => {
    if (!selectedStore || !isDesktop) return null;

    return (
      <div
        style={{
          width: PANEL_WIDTH,
          minWidth: PANEL_WIDTH,
          padding: '16px 16px 16px 0',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}>
        {/* Store info card — only show if there's something to display */}
        {(selectedStore.category || selectedStore.productsStore) && <div style={cardStyle}>
          <div style={sectionLabel}>Store Info</div>
          {selectedStore.category && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 14px',
              borderRadius: '10px',
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
              marginTop: '8px',
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                backgroundColor: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: accentColor,
                flexShrink: 0,
              }}>
                <Icons.Category style={{ fontSize: '16px' }} />
              </div>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 600, color: isDark ? '#e2e8f0' : '#0f172a' }}>
                  {selectedStore.category.name}
                </div>
                <div style={{ fontSize: '12px', color: mutedColor }}>
                  Category
                </div>
              </div>
            </div>
          )}
          {selectedStore.productsStore && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 14px',
              borderRadius: '10px',
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
              marginTop: '8px',
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                backgroundColor: isDark ? 'rgba(16,185,129,0.1)' : '#ecfdf5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#059669',
                flexShrink: 0,
              }}>
                <Icons.Products style={{ fontSize: '16px' }} />
              </div>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 600, color: isDark ? '#e2e8f0' : '#0f172a' }}>
                  Products Store
                </div>
                <div style={{ fontSize: '12px', color: mutedColor }}>
                  Type
                </div>
              </div>
            </div>
          )}
        </div>}

        {/* Reports card */}
        <div style={cardStyle}>
          <div style={sectionLabel}>Reports</div>
          <PanelItem icon={<IChart size={18} />} label="Store Analytics" onClick={() => setOpenAnalyticsDialog(true)} />
          <PanelItem icon={<ICsv size={18} />} label="Generate Reports" disabled={!storeActions} onClick={() => storeActions && storeActions.exportToCsv()} />
          <PanelItem icon={<IQRCode size={18} />} label="Generate Labels" disabled={!storeActions} onClick={() => storeActions && storeActions.generateLabels()} />
        </div>

        {/* Store actions card */}
        <div style={cardStyle}>
          <div style={sectionLabel}>Store</div>
          {permissions.auditStore && (
            <PanelItem icon={<IAudit size={18} />} label="Audit Store" disabled={!storeActions} onClick={() => storeActions && storeActions.auditStore()} />
          )}
          <PanelItem icon={<IEdit size={18} />} label="Edit Store" onClick={() => setOpenEditStoreDialog(true)} />
          <PanelItem icon={<IArchive size={18} />} label="Archive Store" disabled={archiving} onClick={archiveStore} />
        </div>

        {/* Items card */}
        <div style={cardStyle}>
          <div style={sectionLabel}>Items</div>
          {!selectedStore.productsStore && (
            <PanelItem icon={<IPlus size={18} />} label="Add Item" highlight disabled={!storeActions} onClick={() => storeActions && storeActions.addItem()} />
          )}
          <PanelItem icon={<IFolder size={18} />} label="Archived Items" disabled={!storeActions} onClick={() => storeActions && storeActions.openArchives()} />
        </div>
      </div>
    );
  };

  const renderStatCards = () => {
    const cards = [
      { icon: <IBox size={20} />, value: selectedStore.itemCount || 0, label: 'Total Items', accent: 'indigo' },
    ];
    if (selectedStore.totalValue != null) {
      const v = selectedStore.totalValue;
      const display = v >= 1000 ? `${(v / 1000).toFixed(1)}K` : v.toFixed(0);
      cards.push({ icon: <IDollar size={20} />, value: display, label: 'Total Value', accent: 'emerald' });
    }
    if (selectedStore.transactionCount != null) {
      cards.push({ icon: <IActivity size={20} />, value: selectedStore.transactionCount, label: 'Last 30 Days', accent: 'blue' });
    }
    if (selectedStore.outOfStockCount > 0) {
      cards.push({ icon: <IAlert size={20} />, value: selectedStore.outOfStockCount, label: 'Out of Stock', accent: 'rose' });
    }
    return (
      <div>
        <div className="stat-cards-row" style={{
          display: 'flex',
          gap: '12px',
          padding: '16px 20px',
        }}>
          {cards.map((card, i) => (
            <div key={i} className="stat-card-wrapper">
              <StatCard icon={card.icon} value={card.value} label={card.label} accent={card.accent} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
    <style>{`
      @keyframes skPulse {
        0%, 100% { background-color: ${skFrom}; }
        50% { background-color: ${skTo}; }
      }
      .stat-cards-row {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
      }
      .stat-cards-row::-webkit-scrollbar { display: none; }
      .stat-card-wrapper { flex: 1 0 120px; }
      .filter-pills-row::-webkit-scrollbar { display: none; }
    `}</style>
    <View
      style={{ overflow: 'hidden' }}
      icon={
        <span>
          <IStores size={22} />
        </span>
      }
      title={selectedStore ? selectedStore.name : ''}
      subtitle={
        selectedStore && selectedStore.productsStore ? (
          <div
            style={{
              position: 'relative',
              fontSize: '12px',
            }}>
            <Icons.Products /> Products Only Store
          </div>
        ) : (
          ''
        )
      }
      onTitleDropdownClick={isDesktop ? undefined : () => {
        setOpenStoreDialog(true);
      }}
      filterProps={{
        onFilter: (v) => {
          setFilterValue(v);
        },
        enableQrCode: true,
        placeholder: `Search ${
          selectedStore ? (selectedStore.itemCount || 0).toLocaleString() : 0
        } items...`,
        rightContent: isDesktop ? (
          <select
            value={stockFilter || ''}
            onChange={(e) => setStockFilter(e.target.value || null)}
            style={{
              height: '48px',
              padding: '0 12px',
              borderRadius: '12px',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0'}`,
              backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc',
              color: isDark ? '#e2e8f0' : '#334155',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              outline: 'none',
              minWidth: '100px',
              appearance: 'auto',
            }}
          >
            <option value="">All</option>
            <option value="inStock">In Stock</option>
            <option value="outOfStock">Out of Stock</option>
            <option value="lowStock">Low Stock</option>
          </select>
        ) : null,
        bottomContent: !isDesktop ? (
          <div className="filter-pills-row" style={{
            display: 'flex', gap: '6px', marginTop: '10px',
            overflowX: 'auto', WebkitOverflowScrolling: 'touch',
            paddingBottom: '2px',
            scrollbarWidth: 'none',
          }}>
            {[
              { key: null, label: 'All' },
              { key: 'inStock', label: 'In Stock' },
              { key: 'outOfStock', label: 'Out of Stock' },
              { key: 'lowStock', label: 'Low Stock' },
            ].map((opt) => {
              const active = stockFilter === opt.key;
              return (
                <div
                  key={opt.key || 'all'}
                  onClick={() => setStockFilter(opt.key)}
                  style={{
                    padding: '5px 14px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    transition: 'all 0.15s',
                    backgroundColor: active
                      ? (isDark ? 'rgba(99,102,241,0.2)' : '#eef2ff')
                      : (isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'),
                    color: active
                      ? (isDark ? '#a5b4fc' : '#4f46e5')
                      : (isDark ? '#94a3b8' : '#64748b'),
                    border: active
                      ? `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}`
                      : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                  }}
                >
                  {opt.label}
                </div>
              );
            })}
          </div>
        ) : null,
      }}
      onRefresh={async (cb) => {
        await refresh();
        cb();
      }}
      onBack={() => {
        App.eventsManager.emit('event-active-view', StoresView.name, { activeView: 'home' });
      }}
      actionLabel={selectedStore && !selectedStore.productsStore ? 'Add Item' : undefined}
      onAction={selectedStore && !selectedStore.productsStore ? () => { if (storeActions) storeActions.addItem(); } : undefined}
      subHeader={selectedStore && itemsLoaded ? renderStatCards() : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '12px',
          padding: '16px 20px',
        }}>
          {[0, 0.08, 0.16, 0.24].map((d, i) => (
            <div key={i} style={{
              backgroundColor: cardBg,
              borderRadius: '12px',
              border: `1px solid ${cardBorder}`,
              padding: '20px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{
                  width: '80px', height: '12px', borderRadius: 4,
                  backgroundColor: skFrom,
                  animation: `skPulse 2s ease-in-out ${d}s infinite`,
                }} />
                <div style={{
                  width: '44px', height: '44px', borderRadius: 12,
                  backgroundColor: skFrom,
                  animation: `skPulse 2s ease-in-out ${d + 0.05}s infinite`,
                }} />
              </div>
              <div style={{
                width: '90px', height: '26px', borderRadius: 6, marginTop: 16,
                backgroundColor: skFrom,
                animation: `skPulse 2s ease-in-out ${d + 0.1}s infinite`,
              }} />
              <div style={{
                width: '70px', height: '10px', borderRadius: 4, marginTop: 8,
                backgroundColor: skFrom,
                animation: `skPulse 2s ease-in-out ${d + 0.15}s infinite`,
              }} />
            </div>
          ))}
        </div>
      )}
      sidePanel={selectedStore ? renderSidePanel() : renderSidePanelSkeleton()}
      viewId={'stores'}
      showMenu
      activeView={activeView}>
      <Block>
        <Stores
          stores={stores}
          storesLoading={storesLoading}
          storesError={storesError}
          filters={{
            filterValue,
            stockStatus: stockFilter,
          }}
          fetchStores={fetchStores}
          permissions={permissions}
          selectedStore={selectedStore}
          onOpenStoreDialog={() => setOpenStoreDialog(true)}
          hideDrawerFab={isDesktop}
          onActionsReady={setStoreActions}
          onAnalytics={() => setOpenAnalyticsDialog(true)}
          onEditStore={() => setOpenEditStoreDialog(true)}
          onArchiveStore={archiveStore}
          onItemsLoaded={() => setItemsLoaded(true)}
        />
      </Block>
      {openStoreDialog && (
        <StoresDialog
          permissions={permissions}
          open={openStoreDialog}
          stores={stores}
          refreshStores={fetchStores}
          selectedStore={selectedStore}
          onStoreSelect={onStoreSelect}
          onClose={() => {
            setOpenStoreDialog(false);
          }}
        />
      )}
      {openEditStoreDialog && (
        <EditStoreDialog
          open={openEditStoreDialog}
          onEdit={fetchStores}
          store={selectedStore}
          onClose={() => setOpenEditStoreDialog(false)}
        />
      )}
      {openAddStoreDialog && (
        <AddStoreDialog
          open={openAddStoreDialog}
          onAdd={fetchStores}
          onClose={() => setOpenAddStoreDialog(false)}
        />
      )}
      {openAnalyticsDialog && selectedStore && (
        <StoreAnalyticsDialog
          open={openAnalyticsDialog}
          storeId={selectedStore._id}
          onClose={() => setOpenAnalyticsDialog(false)}
        />
      )}
      {openArchivedStoresDialog && (
        <ArchivedStoresDialog
          open={openArchivedStoresDialog}
          onChange={() => fetchStores()}
          onClose={() => setOpenArchivedStoresDialog(false)}
        />
      )}
      {openArchiveConfirm && (
        <Dialog
          open={openArchiveConfirm}
          onClose={() => setOpenArchiveConfirm(false)}
          maxWidth="xs"
          fullWidth>
          <DialogTitle>
            <IArchive size={20} style={{ marginRight: '8px', position: 'relative', top: '3px' }} />
            Archive Store
          </DialogTitle>
          <DialogContent>
            <div style={{ marginBottom: '8px' }}>
              Are you sure you want to archive <strong>{selectedStore && selectedStore.name}</strong>?
            </div>
            <div style={{ fontSize: '13px', color: '#888' }}>
              This store has {selectedStore && (selectedStore.itemCount || 0)} items.
              Items may need to be moved or removed before archiving.
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenArchiveConfirm(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmArchiveStore}
              color="secondary"
              variant="contained"
              disabled={archiving}>
              Archive
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {openWarningsDialog && (
        <StoreArchivesWarningsDialog
          archiveWarnings={archiveWarnings}
          open={openWarningsDialog}
          onClose={() => setOpenWarningsDialog(false)}
        />
      )}
    </View>
    </>
  );
}
StoresView.propTypes = {
  activeView: PropTypes.string,
};

StoresView.defaultProps = {};

export default StoresView;
